import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import GroupOrbitDiagram from "@/components/GroupOrbitDiagram";

const STANDARDS = [
  "Every venture must have a meaningful reason to exist.",
  "Every client relationship must be built on trust.",
  "Every product must unite usefulness with beauty.",
  "Every decision must consider long-term value.",
  "Every expansion must strengthen, not dilute, the group identity.",
];

const OurGroup = () => {
  return (
    <div data-testid="page-our-group">
      <SEO
        title="Our Group"
        description="Gopi Gadhvi Group exists to create, connect and grow ventures that improve how people live, think, feel and build their futures."
        path="/our-group"
      />
      <PageHero
        eyebrow="The Group"
        heading="A House Built to Hold Many Worlds"
        intro="Gopi Gadhvi Group exists to create, connect and grow ventures that improve how people live, think, feel and build their futures. The businesses within the group are different by design. What unites them is a shared commitment to integrity, meaningful creation, intelligent beauty and long-term value."
        minHeight="36vh"
      />

      {/* Thin gold divider -- makes the transition into Philosophy feel
          intentional rather than two disconnected headline blocks. */}
      <div className="page-shell" aria-hidden="true">
        <div className="our-group-divider" />
      </div>

      {/* Philosophy -- positioned on the right side of the page to
          balance the composition against the hero's left-aligned intro
          block above (and the founder-image-right pattern used site-wide). */}
      <section className="relative page-shell py-14 md:py-20 lg:py-24">
        <div className="editorial-col ml-auto text-right">
          <Reveal>
            <span className="eyebrow block mb-5">Philosophy</span>
            <h2 className="text-h2 mb-8">One Vision Should Never Be Confined to One Industry</h2>
            <p className="text-editorial text-justify mb-6">
              Some ideas belong to property. Some belong to spaces. Some become music, books, art
              or tools. Others begin as inward questions and grow into paths of transformation.
            </p>
            <p className="text-editorial text-justify">
              Gopi Gadhvi Group gives each idea the room to become what it is meant to be without
              separating it from the philosophy that created it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Group standard */}
      <section className="relative page-shell py-14 md:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="eyebrow block mb-5">Group Standard</span>
            <h2 className="text-h2 mb-14">Different Expressions.<br />One Standard.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5 text-left">
            {STANDARDS.map((s, i) => (
              <Reveal key={s} delay={i * 60} className={`neu-surface neu-surface-hover p-6 ${i === STANDARDS.length - 1 ? "sm:col-span-2" : ""}`}>
                <p className="text-body">{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem diagram + closing tagline */}
      <section className="relative py-14 md:py-20 lg:py-24" style={{ background: "var(--gg-midnight-black)" }}>
        <div className="page-shell text-center">
          <Reveal className="max-w-2xl mx-auto mb-14">
            <span className="eyebrow block mb-5">Structure</span>
            <h2 className="text-h2 mb-5">How the Group Is Structured</h2>
            <p className="text-editorial mx-auto">
              Property, design, creativity and transformation are the group&rsquo;s four active
              disciplines today. Technology and investment represent where the ecosystem is
              headed next.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <GroupOrbitDiagram />
            <p className="text-small mt-10">Dashed nodes represent future direction, not current operating businesses.</p>
          </Reveal>
          <Reveal delay={160} className="mt-16">
            <p className="font-cormorant italic text-h4" style={{ color: "var(--gg-logo-gold)" }} data-testid="our-group-closing-line">
              Building Trust. Creating Legacy.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default OurGroup;
